<script lang="ts">
  import { afterNavigate, beforeNavigate, goto } from '$app/navigation';

  import { onDestroy } from 'svelte';

  import { ArtAssetType, SaleOrderStatus } from '@do/models';

  import { Breadcrumbs } from '@shared/ui/breadcrumbs';
  import { Button } from '@shared/ui/buttons/button';
  import { Container } from '@shared/ui/container';
  import { Tabs } from '@shared/ui/tabs';

  import { ArtAssetCardApi, type ArtAsset, type SimilarArtAssets } from '@entities/art-asset';
  import { CuratorReview } from '@entities/curators-review';
  import { History, HistoryApi, mapHistoryData, type TokenHistory } from '@entities/history';
  import { purchaseModel, PurchaseState, PURCHASE_UPDATE_INTERVAL_MS } from '@entities/purchase';
  import type { SelectionsReadManyResponse } from '@entities/selection';

  import { ArtworkPreview } from '@features/artwork/artwork-preview';
  import { purchaseConfig } from '@features/purchase/purchase-form';
  import { reportConfig } from '@features/report';
  import { Viewer } from '@features/viewer';

  import { DoBenefits } from '@widgets/do-benefits';
  import { Report } from '@widgets/report';
  // import { OurExpert } from '@widgets/our-expert';
  import { TokenHistorySidebar, tokensHistorySidebarActivity } from '@widgets/token-history-sidebar';

  import ConfirmationModal from './confirmation-modal.svelte';
  import Sale from './sale.svelte';
  import Selections from './selections.svelte';
  import SimilarArtworksTabs from './similar-artworks.svelte';

  export let similarArtworks: SimilarArtAssets;
  export let artwork: ArtAsset;
  export let selectionsData: SelectionsReadManyResponse;

  export let forms: {
    purchase: purchaseConfig.PurchaseForm;
    report: reportConfig.ReportForm;
  };

  const artAssetCardApi = new ArtAssetCardApi(fetch);
  const historyApi = new HistoryApi(fetch);

  let latestTokenHistoryData: TokenHistory['data'] = [];
  let preservedTokenHistoryData: TokenHistory['data'] = [];
  let latestTokenHistoryCursor: string | undefined = '';
  let preservedTokenHistoryCursor: string | undefined = '';

  let imgElement: HTMLImageElement;
  let timer: ReturnType<typeof setTimeout>;
  let isViewerShown = false;
  let showConfirmation = false;
  let previousUrl: string | null = null;

  $: showConfirmation = Array.isArray(artwork.content_flags) && !!artwork.content_flags.length;

  const toggleViewer = () => {
    isViewerShown = !isViewerShown;
  };

  const updateArtAssetData = async (fetchedArtworkData: ArtAsset) => {
    artwork = fetchedArtworkData;

    if (artwork.token_id) {
      refreshHistoryData();
      purchaseModel.set({ state: PurchaseState.Default });
      clearTimeout(timer);
      return;
    }
  };

  const fetchDataPeriodically = async () => {
    try {
      const fetchedArtworkData = await artAssetCardApi.readOne(artwork.id);

      console.log({ artwork }, { fetchedArtworkData });

      const isSaleOrderRealized = fetchedArtworkData.sale_order?.status === SaleOrderStatus.Realized;
      const isFinalizedInBlockchain = fetchedArtworkData.token_owner && fetchedArtworkData.latest_transfer_timestamp;

      if (isSaleOrderRealized) {
        if (isFinalizedInBlockchain) {
          return updateArtAssetData(fetchedArtworkData);
        }

        if ($purchaseModel.state !== PurchaseState.Success) {
          purchaseModel.set({ state: PurchaseState.Success });
        }
      }

      // Fetch again if sale status not changed
      timer = setTimeout(fetchDataPeriodically, PURCHASE_UPDATE_INTERVAL_MS);
    } catch (err) {
      console.error(err);
    }
  };

  async function loadMoreHistoryData() {
    if (!preservedTokenHistoryCursor) return; // no more data to load
    const receivedData = await historyApi.readTokenHistory(artwork.id, preservedTokenHistoryCursor);

    preservedTokenHistoryData = [...preservedTokenHistoryData, ...receivedData.data];
    preservedTokenHistoryCursor = receivedData.cursor || undefined;
  }

  async function refreshHistoryData() {
    const historyData = await historyApi.readTokenHistory(artwork.id, undefined);

    latestTokenHistoryData = historyData.data;
    latestTokenHistoryCursor = historyData.cursor || undefined;
  }

  function handleShowMoreHistoryClick() {
    preservedTokenHistoryData = latestTokenHistoryData;
    preservedTokenHistoryCursor = latestTokenHistoryCursor;
    tokensHistorySidebarActivity.toggle();
  }

  function handleConfirm() {
    showConfirmation = false;
  }

  async function handleClose() {
    if (previousUrl) {
      await goto(previousUrl);
      showConfirmation = false;
    } else {
      history.back();
    }
  }

  afterNavigate(({ from }) => {
    clearTimeout(timer);

    const isOnSale = artwork.sale_order?.status === SaleOrderStatus.Placed;
    // const isSold = artwork.sale_order?.status === SaleOrderStatus.Realized;
    // const isOnAuction = Boolean(artwork.auction?.id);

    if (isOnSale) {
      fetchDataPeriodically();
    }

    // if (isSold || isOnAuction) {
    refreshHistoryData();
    // } else {
    //   // clear data in case if we go to another page w/o history fetching
    //   latestTokenHistoryData = [];
    //   latestTokenHistoryCursor = '';
    // }

    if (from) {
      previousUrl = from.url.toString();
    }
  });

  beforeNavigate(() => {
    clearTimeout(timer);
  });

  onDestroy(() => {
    clearTimeout(timer);
  });
</script>

<Container containerClass="pb-12">
  {#if isViewerShown && (artwork.digital_original?.file_id || artwork.main_photo?.id)}
    <Viewer
      artAssetType="{artwork.art_asset_type}"
      mainPhotoId="{artwork.main_photo?.id}"
      onClose="{toggleViewer}"
      placeholderElement="{imgElement}"
      digitalOriginalData="{artwork.digital_original}"
      artists="{artwork.artists}"
      description="{artwork.name_english ?? artwork.name_original}"
      year="{artwork.creation_year ?? ''}"
      originalSizeX="{artwork.original_size_x}"
      originalSizeY="{artwork.original_size_y}"
      originalUnits="{artwork.original_units}"
      medium="{artwork.medium}"
    />
  {/if}

  <div
    class="mb-12 mt-5 gap-x-0 md:mt-8 md:gap-x-12 xl:mb-20 xl:mt-12 xl:flex xl:gap-10 2xl:grid 2xl:grid-cols-12 2xl:gap-y-5"
  >
    <div
      class="4xl:col-end-10 -mx-5 mb-5 md:-mx-8 md:mb-6 xl:mx-0 xl:w-full 2xl:col-start-1 2xl:col-end-9 2xl:row-start-1 2xl:mx-0 2xl:mb-0"
    >
      <div class="xl:sticky xl:top-[72px]">
        {#key artwork.id}
          <ArtworkPreview
            previewImage="{artwork.main_photo}"
            alt="{artwork.name_english}"
            on:click="{toggleViewer}"
            bind:imgElement
          />
        {/key}
      </div>
    </div>

    <div class="4xl:col-start-10 xl:w-full xl:max-w-[464px] 2xl:col-start-9 2xl:col-end-13 2xl:max-w-full">
      <Breadcrumbs classes="mb-3" />

      <Sale {artwork} purchaseForm="{forms.purchase}" />

      {#if artwork?.curator_review}
        <div class="mb-8">
          <CuratorReview review="{artwork.curator_review}" />
        </div>
      {/if}

      <Tabs
        institutions="{artwork.institutions}"
        descriptionBlocks="{artwork.description?.blocks}"
        locations="{artwork.locations}"
        originalSizeX="{artwork.original_size_x}"
        originalSizeY="{artwork.original_size_y}"
        originalUnits="{artwork.original_units}"
        framedSizeX="{artwork.framed_size_x}"
        framedSizeY="{artwork.framed_size_y}"
        framedSizeZ="{artwork.framed_size_z}"
        creationYear="{artwork.creation_year}"
        styles="{artwork.styles}"
        medium="{artwork.medium}"
        contractAddress="{artwork.contract?.address}"
        contractStandard="{artwork.contract?.standard}"
        contractBlockchain="{artwork.contract?.blockchain}"
        metadataStorage="{artwork.metadata_storage}"
        digitalOriginalData="{artwork.digital_original}"
        tags="{artwork.tags}"
        tokenStatus="{artwork.token_status}"
        tokenHistory="{latestTokenHistoryData}"
        artists="{artwork.artists}"
      >
        <div class="mb-6" slot="history">
          <div class="mb-5">
            <History history="{mapHistoryData(latestTokenHistoryData)}" />
          </div>
          {#if latestTokenHistoryCursor}
            <Button variant="secondary" on:click="{handleShowMoreHistoryClick}">Show more</Button>
          {/if}
        </div>
      </Tabs>

      <div>
        <!-- {#if artwork.institutions}
          <div class="mb-10 flex flex-col gap-5">
            {#each artwork.institutions as institution}
              {@const expert = institution.expert}

              {#if expert}
                <div>
                  <OurExpert
                    name="{expert.full_name}"
                    role="{expert.role}"
                    photo="{expert.avatar}"
                    social="{expert.social_profiles}"
                  />
                </div>
              {/if}
            {/each}
          </div>
        {/if} -->

        <Report form="{forms.report}" formAction="?/report" />
      </div>
    </div>
  </div>

  {#if artwork.art_asset_type === ArtAssetType.DigitalOriginal}
    <div class="mb-12 xl:mb-20">
      <DoBenefits />
    </div>
  {/if}

  {#if similarArtworks.length > 0}
    <div class="w-full">
      <SimilarArtworksTabs similarProducts="{similarArtworks}" />
    </div>
  {/if}

  {#if selectionsData.total_count > 0}
    <Selections {selectionsData} artistName="{artwork.artists[0].name}" />
  {/if}
</Container>

<TokenHistorySidebar
  {latestTokenHistoryData}
  {preservedTokenHistoryData}
  loadMoreFunction="{loadMoreHistoryData}"
  refreshFunction="{refreshHistoryData}"
/>

<ConfirmationModal bind:isOpen="{showConfirmation}" on:confirm="{handleConfirm}" on:close="{handleClose}" />
